import React, { useCallback, useEffect, useState } from "react";
import api from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import { timeAgo, formatDateTime } from "../../inventory/time";
import { validatePassword, PASSWORD_HINT } from "../../utils/passwordRules";
import Icon from "../../components/ui/Icon";
import Modal from "../../components/ui/Modal";
import Toast from "../../components/ui/Toast";
import "../inventory/InventoryPage.css";
import "../plans/Plans.css";
import "./UsersPage.css";

// Panel accounts: everyone who can sign in. Only a superadmin gets here. They
// can add, edit or remove accounts and pick each one's role; they can edit
// their own account but not remove it or change its role.

const ROLES = [
  { value: "ADMIN", label: "Admin", hint: "Sees and edits only the channels and plans they add." },
  { value: "SUPERADMIN", label: "Superadmin", hint: "Sees and edits everything, including history and users." },
];
const roleLabel = (role) => ROLES.find((r) => r.value === role)?.label || role;

const serverMessage = (err, fallback) => err?.response?.data?.message || fallback;

// Adds a user, or edits `user` when given. When editing, an empty password
// keeps the current one.
function UserDialog({ user, isSelf, onClose, onSaved }) {
  const editing = Boolean(user);
  const [form, setForm] = useState({
    email: user?.email || "",
    name: user?.name || "",
    password: "",
    role: user?.role || "ADMIN",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!/^\S+@\S+$/.test(form.email.trim())) next.email = "Enter an email address.";
    if ((!editing || form.password) && !validatePassword(form.password)) next.password = PASSWORD_HINT;
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      const res = editing
        ? await api.put(`/users/${encodeURIComponent(user.id)}`, form)
        : await api.post("/users", form);
      onSaved(res.data);
    } catch (err) {
      setErrors({ form: serverMessage(err, "Could not save the user. Try again.") });
      setSaving(false);
    }
  };

  return (
    <Modal
      title={editing ? "Edit user" : "Add user"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="user-form" className="btn-primary" disabled={saving}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add user"}
          </button>
        </>
      }
    >
      <form id="user-form" className="form-grid" onSubmit={submit} noValidate>
        <div className="form-field span-2">
          <label htmlFor="uf-email">
            Email <span className="req">*</span>
          </label>
          <input
            id="uf-email"
            type="email"
            autoComplete="off"
            className={`form-input${errors.email ? " has-error" : ""}`}
            value={form.email}
            onChange={set("email")}
            placeholder="name@admongrel.com"
          />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>

        <div className="form-field span-2">
          <label htmlFor="uf-name">Name</label>
          <input
            id="uf-name"
            autoComplete="off"
            className="form-input"
            value={form.name}
            onChange={set("name")}
            placeholder="Shown in the change history"
          />
        </div>

        <div className="form-field span-2">
          <span className="form-label">Role</span>
          {isSelf ? (
            <span className="form-hint">
              {roleLabel(form.role)}. You can't change your own role.
            </span>
          ) : (
            <div className="role-options" role="radiogroup" aria-label="Role">
              {ROLES.map((r) => (
                <label key={r.value} className={`role-option${form.role === r.value ? " is-on" : ""}`}>
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={form.role === r.value}
                    onChange={set("role")}
                  />
                  <span>
                    <strong>{r.label}</strong>
                    {r.hint}
                  </span>
                </label>
              ))}
            </div>
          )}
          {editing && !isSelf && user.role !== form.role && (
            <span className="form-hint">Changing the role signs them out; it applies from their next sign-in.</span>
          )}
        </div>

        <div className="form-field span-2">
          <label htmlFor="uf-password">
            {editing ? (
              "New password"
            ) : (
              <>
                First password <span className="req">*</span>
              </>
            )}
          </label>
          <input
            id="uf-password"
            type="password"
            autoComplete="new-password"
            className={`form-input${errors.password ? " has-error" : ""}`}
            value={form.password}
            onChange={set("password")}
          />
          {errors.password ? (
            <span className="form-error">{errors.password}</span>
          ) : (
            <span className="form-hint">
              {editing
                ? "Leave empty to keep the current password. A new one signs them out on every device."
                : `${PASSWORD_HINT} Share it with them; they can change it with "Forgot your password?".`}
            </span>
          )}
        </div>

        {errors.form && (
          <div className="form-field span-2">
            <span className="form-error">{errors.form}</span>
          </div>
        )}
      </form>
    </Modal>
  );
}

export default function UsersPage() {
  const { user: me, login } = useAuth();
  const [users, setUsers] = useState(null); // null while loading
  const [loadFailed, setLoadFailed] = useState(false);
  const [dialog, setDialog] = useState(null); // { user? }: add, or edit `user`
  const [confirm, setConfirm] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);
  const clearToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saved = (user) => {
    const editing = Boolean(dialog.user);
    setUsers((list) =>
      [...list.filter((u) => u.id !== user.id), user].sort((a, b) => a.email.localeCompare(b.email))
    );
    // The top bar shows who is signed in
    if (user.id === me?.id) login({ ...me, email: user.email, name: user.name });
    setDialog(null);
    setToast({ message: editing ? `Saved ${user.email}` : `Added ${user.email}` });
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${encodeURIComponent(confirm.id)}`);
      setUsers((list) => list.filter((u) => u.id !== confirm.id));
      setToast({ message: `Deleted ${confirm.email}` });
      setConfirm(null);
    } catch (err) {
      setDeleteError(serverMessage(err, "Could not delete the user. Try again."));
    }
  };

  let body;
  if (loadFailed) {
    body = (
      <div className="inv-empty">
        <h2>Could not load users</h2>
        <p>The server did not answer. Check your connection and try again.</p>
        <div className="inv-empty-actions">
          <button type="button" className="btn" onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!users) {
    body = (
      <div className="inv-empty">
        <p>Loading users…</p>
      </div>
    );
  } else {
    body = (
      <div className="table-wrap">
        <table className="inv-table plans-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Last sign-in</th>
              <th>Added</th>
              <th className="col-actions">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="plan-link">
                    {u.name || u.email}
                    {u.id === me?.id && <span className="status status-draft user-you">You</span>}
                  </div>
                  {u.name && <div className="plan-client">{u.email}</div>}
                </td>
                <td>
                  <span className={`status ${u.role === "SUPERADMIN" ? "status-sent" : "status-draft"}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td title={formatDateTime(u.lastLoginAt)}>{u.lastLoginAt ? timeAgo(u.lastLoginAt) : "Never"}</td>
                <td title={formatDateTime(u.createdAt)}>{timeAgo(u.createdAt)}</td>
                <td className="col-actions">
                  <div className="row-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      aria-label={`Edit ${u.email}`}
                      onClick={() => setDialog({ user: u })}
                    >
                      <Icon name="edit" />
                    </button>
                    {u.id !== me?.id && (
                      <button
                        type="button"
                        className="icon-btn danger"
                        title="Delete"
                        aria-label={`Delete ${u.email}`}
                        onClick={() => {
                          setDeleteError("");
                          setConfirm(u);
                        }}
                      >
                        <Icon name="trash" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="plans">
      <div className="inv-head">
        <div>
          <h1>Users</h1>
          <p>Everyone who can sign in to Adventory.</p>
        </div>
        <div className="inv-actions">
          <button type="button" className="btn-primary" onClick={() => setDialog({})}>
            <Icon name="plus" /> Add user
          </button>
        </div>
      </div>

      <div className="inv-card">{body}</div>

      {dialog && (
        <UserDialog
          user={dialog.user}
          isSelf={dialog.user?.id === me?.id}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}

      {confirm && (
        <Modal
          title="Delete user?"
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={remove}>
                Delete user
              </button>
            </>
          }
        >
          <p className="modal-text">
            <strong>{confirm.email}</strong> will no longer be able to sign in, and is signed out
            everywhere now. Their changes stay in the history.
          </p>
          {deleteError && <p className="form-error">{deleteError}</p>}
        </Modal>
      )}

      <Toast toast={toast} onDone={clearToast} />
    </div>
  );
}
